import { unwrapMonarchScalar } from "@/js/helpers/unwrapMonarchScalar";

/** @param {unknown} raw */
const pickString = (raw) => {
  const v = unwrapMonarchScalar(raw) ?? (typeof raw === "string" ? raw : null);
  if (v == null) return undefined;
  const t = String(v).trim();
  return t.length ? t : undefined;
};

/**
 * Monarch booleans: plain `true`/`false`, `{ value: true }`, or string `"true"` / `"false"`.
 *
 * @param {unknown} raw
 */
const pickBool = (raw) => {
  if (raw === true || raw === false) return raw;
  if (raw && typeof raw === "object" && !Array.isArray(raw) && "value" in raw) {
    const val = raw.value;
    if (val === true || val === false) return val;
    if (typeof val === "string") return val.toLowerCase() === "true";
  }
  const s = unwrapMonarchScalar(raw);
  if (typeof s === "string") return s.toLowerCase() === "true";
  return false;
};

const DEFAULT_FEATURE_HIGHLIGHTS = [
  { iconClass: "icon-fiber", copy: "Plan specs" },
  { iconClass: "icon-wrench", copy: "Install details" },
  { iconClass: "icon-invoice", copy: "Billing info" },
];

const ICON_KEY_TO_CLASS = {
  fiber: "icon-fiber",
  wrench: "icon-wrench",
  invoice: "icon-invoice",
  offers: "icon-offers",
  "price-circle": "icon-price-circle",
  router: "icon-router",
  invoice: "icon-invoice",
  "speed-guage": "icon-speed-guage",
  "location-marker": "icon-location-marker",
  "location-marker2": "icon-location-marker2",
};

/** @param {unknown} raw */
const iconKeyToClass = (raw) => {
  const s = String(unwrapMonarchScalar(raw) ?? raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/^icon-/, "");
  return ICON_KEY_TO_CLASS[s] || "icon-fiber";
};

/**
 * Monarch may wrap list fields as `{ value: [...] }`.
 *
 * @param {unknown} raw
 * @returns {unknown}
 */
const unwrapMonarchList = (raw) => {
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    Array.isArray(raw.value)
  ) {
    return raw.value;
  }
  return raw;
};

/**
 * Monarch behavior:
 * - Section **disabled** → omits `featureHighlights` key → no rows (same as off).
 * - Section **enabled, no items** → `featureHighlights: []` → no rows.
 * - **`proactiveModalContent` absent** (`pmc` null/undefined) → legacy defaults for heading/modal fallbacks.
 *
 * @param {Record<string, unknown> | null | undefined} pmc
 * @returns {Array<{ iconClass: string, copy: string }>}
 */
const featureHighlights = (pmc) => {
  if (!pmc || typeof pmc !== "object") {
    return DEFAULT_FEATURE_HIGHLIGHTS;
  }

  if (
    Object.prototype.hasOwnProperty.call(pmc, "showFeatureHighlights") &&
    !pickBool(pmc.showFeatureHighlights)
  ) {
    return [];
  }

  // Monarch: section disabled → key omitted. Section on but no items → `featureHighlights: []`.
  if (!Object.prototype.hasOwnProperty.call(pmc, "featureHighlights")) {
    return [];
  }

  const raw = unwrapMonarchList(pmc.featureHighlights);

  if (raw === false || raw === null) {
    return [];
  }

  if (Array.isArray(raw) && raw.length === 0) {
    return [];
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  const out = [];
  raw.forEach((row) => {
    if (!row || typeof row !== "object") {
      return;
    }
    const copy = pickString(row.copy);
    if (!copy) {
      return;
    }
    out.push({ iconClass: iconKeyToClass(row.icon), copy });
  });

  return out;
};

/** @param {Record<string, unknown> | null | undefined} pmc */
const stepsList = (pmc) => {
  const raw = pmc?.steps;
  if (!Array.isArray(raw)) return [];
  return raw.map((s) => pickString(s)).filter(Boolean);
};

/** @param {unknown} raw */
const ctaColors = (raw) => {
  const key = String(unwrapMonarchScalar(raw) ?? raw ?? "")
    .trim()
    .toLowerCase();
  if (key === "electricity") {
    return {
      background: "var(--color-electric-blue)",
      color: "var(--color-midnight-express)",
    };
  }
  return {
    background: "var(--color-primary)",
    color: "var(--color-white)",
  };
};

/**
 * Monarch `proactiveModalContent` → props for ProactiveChatModal (single entry point).
 *
 * @param {Record<string, unknown> | null | undefined} proactiveModalContent
 */
export const resolveProactiveModalPresentation = (proactiveModalContent) => {
  const pmc = proactiveModalContent;

  const cta = pmc?.cta;
  const colors = ctaColors(
    cta && typeof cta === "object" ? cta.backgroundColor : undefined,
  );

  return {
    heading: pickString(pmc?.heading?.copy),
    subheading: pickString(pmc?.subheading?.copy),
    featureHighlights: featureHighlights(pmc),
    steps: stepsList(pmc),
    helperText: pickString(pmc?.helperText),
    ctaCopy: cta && typeof cta === "object" ? pickString(cta.copy) : undefined,
    ctaButtonStyle: {
      backgroundColor: colors.background,
      color: colors.color,
    },
    darkMode: pickBool(pmc?.darkMode),
    showHeaderBrand: pickBool(pmc?.icon),
  };
};
